import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExerciseCard, PairButton, type PairButtonState } from '@fluent/ui';
import type { TapPairs as TapPairsExercise } from '@fluent/types';
import { type MatchEntry, shuffleMatchColumns } from './shuffle.js';
import { playSfx } from './sounds.js';
import { playClip } from '../audio/engine.js';
import './TapPairs.css';

export interface TapPairsProps {
  exercise: TapPairsExercise;
  locked: boolean;
  /** Fires once when every pair has been matched. `hadWrong` is true if
   * the user mis-matched at any point during play — used by the reducer
   * to set first-try status. */
  onComplete: (summary: { hadWrong: boolean }) => void;
}

/**
 * TapPairs — two columns (English ↔ Georgian) of the same N pair items,
 * shuffled independently. User taps one from each side; correct matches
 * remove both; wrong matches flash red briefly then reset.
 *
 * Column order comes from `shuffleMatchColumns`, which guarantees a visibly
 * mixed layout (left never in authored order; no correct pair on the same
 * row) — a plain seeded shuffle can otherwise land on an order that reads as
 * "barely mixed."
 *
 * Uncontrolled: owns its own selection state. Parent mounts with
 * `key={exercise.id}` so state resets on exercise change. Parent is
 * notified once via `onComplete` when all pairs are matched.
 */
export function TapPairs({ exercise, locked, onComplete }: TapPairsProps) {
  const { t } = useTranslation();

  const { left, right } = useMemo<{ left: MatchEntry[]; right: MatchEntry[] }>(
    () => shuffleMatchColumns(exercise.pairs, exercise.id),
    [exercise.id, exercise.pairs],
  );

  const [matched, setMatched] = useState<Set<number>>(() => new Set());
  const [selLeft, setSelLeft] = useState<number | null>(null);
  const [selRight, setSelRight] = useState<number | null>(null);
  const [wrongFlash, setWrongFlash] = useState<{ left: number; right: number } | null>(null);
  const hadWrongRef = useRef(false);
  const completedRef = useRef(false);
  const flashTimerRef = useRef<number | null>(null);

  // Fire onComplete exactly once when all pairs are matched.
  useEffect(() => {
    if (matched.size !== exercise.pairs.length || completedRef.current) return;
    completedRef.current = true;
    // Defer so the UI gets one frame to show the final "matched" state.
    const id = window.setTimeout(() => {
      onComplete({ hadWrong: hadWrongRef.current });
    }, 180);
    return () => window.clearTimeout(id);
  }, [matched, exercise.pairs.length, onComplete]);

  // Whenever both sides are selected, evaluate.
  useEffect(() => {
    if (selLeft === null || selRight === null) return;
    const leftEntry = left[selLeft];
    const rightEntry = right[selRight];
    if (leftEntry.pairIndex === rightEntry.pairIndex) {
      playSfx('correct');
      // Reinforce pronunciation: fire-and-forget play of the matched English.
      // Phase 8 ships audio per pair so users hear "hello" the moment they
      // match it. Routed through the shared Web Audio engine. No state
      // coupling — quick, throwaway.
      const pair = exercise.pairs[leftEntry.pairIndex];
      if (pair?.audio_key) {
        playClip(pair.audio_key);
      }
      setMatched((prev) => {
        const next = new Set(prev);
        next.add(leftEntry.pairIndex);
        return next;
      });
      setSelLeft(null);
      setSelRight(null);
    } else {
      playSfx('wrong');
      hadWrongRef.current = true;
      setWrongFlash({ left: selLeft, right: selRight });
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = window.setTimeout(() => {
        setSelLeft(null);
        setSelRight(null);
        setWrongFlash(null);
      }, 500);
    }
  }, [selLeft, selRight, left, right, exercise.pairs]);

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    };
  }, []);

  const tapLeft = (i: number) => {
    if (locked || completedRef.current) return;
    if (matched.has(left[i].pairIndex)) return;
    if (wrongFlash) return; // block during flash
    setSelLeft((prev) => (prev === i ? null : i));
  };

  const tapRight = (i: number) => {
    if (locked || completedRef.current) return;
    if (matched.has(right[i].pairIndex)) return;
    if (wrongFlash) return;
    setSelRight((prev) => (prev === i ? null : i));
  };

  const stateFor = (side: 'left' | 'right', i: number, entry: MatchEntry): PairButtonState => {
    if (matched.has(entry.pairIndex)) return 'matched';
    const isFlashingWrong = wrongFlash && (side === 'left' ? wrongFlash.left === i : wrongFlash.right === i);
    if (isFlashingWrong) return 'wrong';
    const isSelected = side === 'left' ? selLeft === i : selRight === i;
    if (isSelected) return 'selected';
    return 'idle';
  };

  return (
    <ExerciseCard kicker={t('lesson.tap_pairs.kicker')} prompt={t('lesson.tap_pairs.prompt')}>
      <div
        className="fluent-tpairs"
        data-testid="tap-pairs"
        aria-label={t('lesson.tap_pairs.grid_aria')}
      >
        <ul className="fluent-tpairs__col" aria-label="English" lang="en">
          {left.map((entry, i) => (
            <li key={`l-${i}`} className="fluent-tpairs__slot">
              <PairButton
                state={stateFor('left', i, entry)}
                onClick={() => tapLeft(i)}
                disabled={locked || matched.has(entry.pairIndex)}
                data-testid={`pair-en-${entry.text}`}
              >
                {entry.text}
              </PairButton>
            </li>
          ))}
        </ul>

        <ul className="fluent-tpairs__col" aria-label="ქართული" lang="ka">
          {right.map((entry, i) => (
            <li key={`r-${i}`} className="fluent-tpairs__slot">
              <PairButton
                state={stateFor('right', i, entry)}
                onClick={() => tapRight(i)}
                disabled={locked || matched.has(entry.pairIndex)}
                data-testid={`pair-ka-${entry.text}`}
              >
                {entry.text}
              </PairButton>
            </li>
          ))}
        </ul>
      </div>
    </ExerciseCard>
  );
}
